import { ActivatedRouteSnapshot, PRIMARY_OUTLET, RouterStateSnapshot } from '@angular/router';
import { WebPaths } from '@partner/shared/SharedConstants';
import { AppSidenavItem, AppSidenavSubItem } from 'common';
import { DASHBOARD_PAGES, DASHBOARDS_NAV_LABEL, DashboardPageDefinition } from '../dashboard.data';

/**
 * Maps a router url to the dashboard nav label it should highlight:
 * `/dashboard` (with or without query) → `DASHBOARDS_NAV_LABEL`, `/dashboard/<slug>` → that page's `navLabel`,
 * anything else → `null`.
 */
export function dashboardNavLabelForUrl(url: string): string | null {
  const path = url.split(/[?#]/)[0];
  if (path === WebPaths.Dashboard) {
    return DASHBOARDS_NAV_LABEL;
  }
  const page = DASHBOARD_PAGES.find((candidate) => WebPaths.DashboardPage(candidate.slug) === path);
  return page?.navLabel ?? null;
}

/**
 * Returns new item objects with `selected` set from a dashboard nav label: the parent is selected only for
 * `DASHBOARDS_NAV_LABEL`, a Dashboards sub-item on label match, everything else is `false`. `null` clears all.
 */
export function markDashboardSelection(items: AppSidenavItem[], label: string | null): AppSidenavItem[] {
  return items.map((item) => {
    const isDashboards = item.label === DASHBOARDS_NAV_LABEL;
    const marked: AppSidenavItem = { ...item, selected: isDashboards && label === DASHBOARDS_NAV_LABEL };
    if (item.subitems) {
      marked.subitems = item.subitems.map(
        (subitem): AppSidenavSubItem => ({
          ...subitem,
          selected: isDashboards && label !== null && subitem.label === label,
        }),
      );
    }
    return marked;
  });
}

/**
 * Descends the primary-outlet chain of the router state and returns the first `data.dashboardPage` found.
 * Never uses `firstChild`: the app shell lists named-outlet siblings (app-bar, sidenav-content) next to the primary child.
 */
export function findDashboardPage(snapshot: RouterStateSnapshot): DashboardPageDefinition | null {
  let node: ActivatedRouteSnapshot | undefined = snapshot.root;
  while (node) {
    const page = node.data?.dashboardPage as DashboardPageDefinition | undefined;
    if (page) {
      return page;
    }
    node = node.children.find((child) => child.outlet === PRIMARY_OUTLET);
  }
  return null;
}

/** Climbs `parent` from the given route until a resolver has left `data.appSidenav.items`; `null` when none has. */
export function findSidenavItems(route: ActivatedRouteSnapshot): AppSidenavItem[] | null {
  let node: ActivatedRouteSnapshot | null = route;
  while (node) {
    const items = node.data?.appSidenav?.items as AppSidenavItem[] | undefined;
    if (items) {
      return items;
    }
    node = node.parent;
  }
  return null;
}
